import React from 'react'
import {injectIntl} from "react-intl";
import {connect} from "react-redux";

/**
 * Fare total + compact vehicle capacity (passengers, luggage, wheelchair).
 * Flex row with alignItems center so fare text lines up vertically with icon counts; capacity stats share one color and font size.
 */
const TripDetailsBody = ({trip}) => {
    const {car, fare} = trip;

    const fareTotalRaw = fare && fare.total;
    const fareTotalNumber = typeof fareTotalRaw === 'number' ? fareTotalRaw : Number(fareTotalRaw);
    const showFare = Number.isFinite(fareTotalNumber);

    const maxPassengers = car && typeof car.maxPassengers === 'number' ? car.maxPassengers : Number(car && car.maxPassengers) || 0;
    const maxLuggage = car && typeof car.maxLuggage === 'number' ? car.maxLuggage : Number(car && car.maxLuggage) || 0;
    const wheelchairRaw = car ? (car.maxWeelchairs ?? car.maxWheelchairs) : undefined;
    const wheelchairCount = typeof wheelchairRaw === 'number' ? wheelchairRaw : Number(wheelchairRaw) || 0;
    const hasWheelchairCapacity = wheelchairCount > 0;

    const rowStyle = {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: showFare && car ? 'space-between' : showFare ? 'flex-start' : 'flex-end',
        gap: '10px 16px',
        padding: '14px 16px',
        margin: '0',
        borderTop: '1px solid #e0e0d8',
        borderBottom: '1px solid #e0e0d8',
        backgroundColor: '#f5f5f1',
        boxSizing: 'border-box',
        width: '100%',
    };

    const fareBlockStyle = {
        display: 'flex',
        alignItems: 'center',
        flex: showFare && car ? '1 1 auto' : '0 1 auto',
        minWidth: 0,
        minHeight: '28px',
    };

    const fareStyle = {
        fontSize: '14px',
        fontWeight: 600,
        color: '#2d2d2d',
        letterSpacing: '0.01em',
        margin: 0,
        padding: 0,
        lineHeight: 1.2,
        textAlign: 'left',
    };

    const statsStyle = {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '16px',
        justifyContent: 'flex-end',
        flex: '0 0 auto',
    };

    // Same typography and color for all capacity stats (including wheelchair) for visual consistency and readability.
    const statClusterStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: 1,
        color: '#c45c12',
    };

    if (!showFare && !car) {
        return null;
    }

    return (
        <div className="trip-details-fare-capacity" style={rowStyle} role="region" aria-label="Fare and vehicle capacity">
            {showFare && (
                <div style={fareBlockStyle}>
                    <p style={fareStyle}>
                        Fare:{' '}
                        <span style={{color: '#1a1a1a', fontVariantNumeric: 'tabular-nums'}}>
                            ${fareTotalNumber.toFixed(2)}
                        </span>
                    </p>
                </div>
            )}
            {car && (
                <div style={{
                    ...statsStyle,
                    ...(!showFare ? {width: '100%', justifyContent: 'flex-end'} : {}),
                }}>
                    <span style={statClusterStyle} aria-label={`Passenger capacity: ${maxPassengers}`}>
                        <span className="icon icon-themeenergy_user-3" aria-hidden="true" style={{color: '#c45c12'}}/>
                        <span style={{fontVariantNumeric: 'tabular-nums', color: '#c45c12'}}>{maxPassengers}</span>
                    </span>
                    <span style={statClusterStyle} aria-label={`Luggage capacity: ${maxLuggage}`}>
                        <span className="icon icon-themeenergy_travel-bag" aria-hidden="true" style={{color: '#c45c12'}}/>
                        <span style={{fontVariantNumeric: 'tabular-nums', color: '#c45c12'}}>{maxLuggage}</span>
                    </span>
                    <span
                        style={statClusterStyle}
                        aria-label={
                            hasWheelchairCapacity
                                ? `Wheelchair capacity: ${wheelchairCount}`
                                : 'Wheelchair capacity: none for this vehicle'
                        }
                    >
                        <span className="icon icon-themeenergy_patient-wheelchair" aria-hidden="true" style={{color: '#c45c12'}}/>
                        <span style={{fontVariantNumeric: 'tabular-nums', color: '#c45c12'}}>{wheelchairCount}</span>
                    </span>
                </div>
            )}
        </div>
    );
};

function mapStateToProps({ui, user}) {
    const {lists} = user;
    const {general} = ui;
    return {
        lists,
        general
    }
}

export default injectIntl(connect(mapStateToProps)(TripDetailsBody))
